import { Directive, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { DestroyController } from '@celerofinancas/class-utils';
import { DeleteUserAction } from '@celerofinancas/state-management';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';
import { Store } from '@ngxs/store';
import { AuthService } from '@shared/services/auth.service';
import { EMPTY, catchError, takeUntil } from 'rxjs';
import { TokenExchangeEvents } from './token-exchange-events';

/**
 * Base directive for every screen the host portal embeds: receives the portal's
 * token through an attribute and trades it for a session.
 *
 * ## Why an attribute
 *
 * The portal owns the login. It renders the tag with `token="..."` and Angular
 * Elements turns the attribute into this `@Input`. There is no other channel: the
 * component runs inside the portal's page, cannot see its cookies, and must not
 * show a login screen of its own.
 *
 * Same design as `TokenExchange` in `serasa-cl-webcomponents`, deliberately —
 * whoever integrates the portal already knows this contract, and diverging would
 * mean a second integration to learn.
 *
 * ## Why `ngOnChanges` and not `ngOnInit`
 *
 * The attribute frequently arrives **after** the element is in the DOM: the portal
 * mounts the tag and fills the token when its own session resolves. On `ngOnInit`
 * the value would still be empty and the exchange would never happen.
 *
 * ## Why it re-checks the session
 *
 * The guard is `!accessToken || !isAccessTokenValid`, not just "a token arrived".
 * A page carrying several of these elements would otherwise burn one exchange per
 * component on every attribute write, each returning a **different** session.
 * `AuthService` de-duplicates concurrent calls; this check stops the sequential
 * ones.
 */
@Directive()
export class TokenExchange extends DestroyController implements OnChanges {
  /**
   * Token issued by the host portal's identity provider.
   */
  @Input()
  public token = '';

  /**
   * Exchange service.
   */
  private readonly auth = inject(AuthService);

  /**
   * Token storage and session events.
   */
  private readonly tokens = inject(TokenHandlerService);

  /**
   * NGXS store, cleared when the exchange fails.
   */
  protected readonly store = inject(Store);

  /**
   * Runs the exchange when a token arrives and there is no usable session.
   *
   * On failure it emits `TOKEN_EXCHANGED_FAILED` before anything else: that event
   * is what `FailureWrapperComponent` renders the error screen from, and what
   * releases the requests `TokenRaceConditionInterceptor` is holding. Without it
   * the screen would sit empty forever, waiting for a token that is not coming.
   *
   * @param changes bound input changes, read for `token`
   */
  public ngOnChanges(changes: SimpleChanges): void {
    const incoming = changes['token']?.currentValue as string | undefined;

    if (!incoming || (this.tokens.accessToken && this.tokens.isAccessTokenValid)) {
      return;
    }

    this.auth
      .tokenExchange(incoming)
      .pipe(
        catchError(() => {
          this.tokens.eventsSubject$.next({ event: TokenExchangeEvents.TOKEN_EXCHANGED_FAILED });
          this.tokens.clearTokensFromStorage();
          this.store.dispatch(new DeleteUserAction());

          // Completes instead of re-throwing — the one deliberate divergence from
          // serasa's version. There the `catchError` ends in `throwError` and the
          // `subscribe` below has no error handler, so RxJS reports the rejection as
          // an unhandled error: it escapes to `window.onerror`, and inside the host
          // portal that means our failed exchange surfaces as a global error in
          // someone else's page. Nothing was chaining off this stream anyway — the
          // three lines above are the whole handling, and `TOKEN_EXCHANGED_FAILED`
          // is what the rest of the app reacts to.
          return EMPTY;
        }),
        takeUntil(this.destroyController$),
      )
      .subscribe(({ body }) => this.tokens.storeTokens(body));
  }
}
